import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import { useMutation, useQueries, useQuery, useQueryClient } from '@tanstack/react-query';
import type { ScreenGroup } from '@ghostscreen/contracts';
import { QueryErrorState } from '../components/dashboard/QueryErrorState';
import { QueryLoadingBlock } from '../components/dashboard/QueryLoadingBlock';
import { apiErrorMessage } from '../i18n';
import { api } from '../lib/api';
import { useQueryRetry } from '../lib/dashboard/useQueryRetry';
import { useOrganisationId } from '../lib/session';
import { useScreenPlaylistAssignments } from './playlists/useScreenPlaylistAssignments';
import {
  buildGroupCardModel,
  buildGroupPresets,
  type GroupScreenPreset,
} from './groups/buildGroupCardModel';
import { GroupCardGrid } from './groups/GroupCardGrid';
import { GroupDrawer } from './groups/GroupDrawer';
import { GroupsEmptyState } from './groups/GroupsEmptyState';

/**
 * Groups page — cards + create/edit drawer (Ghost Screen Groups.dc.html).
 */
export function GroupsPage() {
  const { t } = useTranslation('groups');
  const organisationId = useOrganisationId();
  const queryClient = useQueryClient();
  const location = useLocation();
  const navigate = useNavigate();
  const [drawerMode, setDrawerMode] = useState<'closed' | 'new' | string>('closed');
  const [createError, setCreateError] = useState<string | null>(null);

  const groupsQuery = useQuery({
    queryKey: ['screen-groups', organisationId],
    queryFn: () => api.listScreenGroups(organisationId),
  });
  const screensQuery = useQuery({
    queryKey: ['screens', organisationId],
    queryFn: () => api.listScreens(organisationId),
  });
  const locationsQuery = useQuery({
    queryKey: ['locations', organisationId],
    queryFn: () => api.listLocations(organisationId),
  });
  const playlistsQuery = useQuery({
    queryKey: ['playlists', organisationId],
    queryFn: () => api.listPlaylists(organisationId),
  });
  const assignments = useScreenPlaylistAssignments(organisationId);

  const groups = groupsQuery.data ?? [];
  const screens = screensQuery.data ?? [];
  const locations = locationsQuery.data ?? [];
  const playlists = playlistsQuery.data ?? [];

  const detailQueries = useQueries({
    queries: groups.map((group) => ({
      queryKey: ['screen-group', organisationId, group.id],
      queryFn: () => api.getScreenGroup(organisationId, group.id),
    })),
  });

  const models = useMemo(
    () =>
      groups.map((group, index) =>
        buildGroupCardModel({
          group,
          memberIds: detailQueries[index]?.data?.screenIds ?? [],
          screens,
          assignmentByScreenId: assignments.assignmentByScreenId,
        }),
      ),
    [groups, detailQueries, screens, assignments.assignmentByScreenId],
  );

  const presets = useMemo(
    () => buildGroupPresets({ screens, locations }),
    [screens, locations],
  );

  const editingGroup: ScreenGroup | null =
    drawerMode !== 'closed' && drawerMode !== 'new'
      ? (groups.find((group) => group.id === drawerMode) ?? null)
      : null;

  const invalidate = async () => {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: ['screen-groups', organisationId] }),
      queryClient.invalidateQueries({ queryKey: ['screen-group', organisationId] }),
      queryClient.invalidateQueries({ queryKey: ['screen-assignment', organisationId] }),
    ]);
  };

  const quickCreate = useMutation({
    mutationFn: async (preset: GroupScreenPreset) => {
      const created = await api.createScreenGroup(organisationId, { name: preset.name });
      for (const screenId of preset.screenIds) {
        await api.addScreenGroupMember(organisationId, created.id, { screenId });
      }
      return created;
    },
    onSuccess: async () => {
      setCreateError(null);
      await invalidate();
    },
    onError: (err) => setCreateError(apiErrorMessage(err, t('createError'))),
  });

  useEffect(() => {
    if (location.hash !== '#new') return;
    setDrawerMode('new');
    navigate({ pathname: '/groups', search: location.search }, { replace: true });
  }, [location.hash, location.search, navigate]);

  const loading =
    groupsQuery.isPending ||
    screensQuery.isPending ||
    locationsQuery.isPending ||
    playlistsQuery.isPending;
  const failed =
    groupsQuery.isError ||
    screensQuery.isError ||
    locationsQuery.isError ||
    playlistsQuery.isError;

  const { isRetrying, retry } = useQueryRetry(async () => {
    await Promise.all([
      groupsQuery.refetch(),
      screensQuery.refetch(),
      locationsQuery.refetch(),
      playlistsQuery.refetch(),
    ]);
  });

  return (
    <div className="flex flex-col gap-[22px]">
      {createError ? (
        <p className="m-0 text-sm" style={{ color: 'var(--gs-bad)' }} role="alert">
          {createError}
        </p>
      ) : null}

      {failed || isRetrying ? (
        <QueryErrorState
          message={t('loadError')}
          onRetry={retry}
          isRetrying={isRetrying}
        />
      ) : loading ? (
        <QueryLoadingBlock />
      ) : groups.length === 0 ? (
        <GroupsEmptyState
          starters={presets}
          busy={quickCreate.isPending}
          onNew={() => setDrawerMode('new')}
          onStarter={(preset) => quickCreate.mutate(preset)}
        />
      ) : (
        <GroupCardGrid
          organisationId={organisationId}
          models={models}
          playlists={playlists}
          onEdit={(groupId) => setDrawerMode(groupId)}
          onNew={() => setDrawerMode('new')}
          onAssigned={() => {
            void invalidate();
          }}
        />
      )}

      <GroupDrawer
        open={drawerMode !== 'closed'}
        organisationId={organisationId}
        group={editingGroup}
        screens={screens}
        locations={locations}
        onClose={() => setDrawerMode('closed')}
      />
    </div>
  );
}
